/**
 * Product Redux slice
 * @module features/products/productSlice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import productService from './productService';

const initialState = {
  products: [],
  product: null,
  categories: [],
  filters: { industries: [], resinTypes: [], pigmentTypes: [] },
  pagination: null,
  isLoading: false,
  error: null,
};

const normalizeItems = (payload, legacyKey) => {
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.[legacyKey])) return payload[legacyKey];
  return [];
};

export const fetchProducts = createAsyncThunk(
  'products/fetchAll',
  async (params, thunkAPI) => {
    try {
      return await productService.getProducts(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllProducts = createAsyncThunk(
  'products/fetchAllPages',
  async (params, thunkAPI) => {
    try {
      return await productService.getAllProducts(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchProductBySlug = createAsyncThunk(
  'products/fetchBySlug',
  async (slug, thunkAPI) => {
    try {
      return await productService.getProductBySlug(slug);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchFilterOptions = createAsyncThunk(
  'products/fetchFilters',
  async (_, thunkAPI) => {
    try {
      return await productService.getFilterOptions();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, thunkAPI) => {
    try {
      return await productService.getCategories();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearProduct: (state) => {
      state.product = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = normalizeItems(action.payload, 'products');
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = normalizeItems(action.payload, 'products');
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchProductBySlug.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.isLoading = false;
        state.product = action.payload?.item || action.payload?.product || null;
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filters = action.payload?.filters || state.filters;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = normalizeItems(action.payload, 'categories');
      });
  },
});

export const { clearProduct, clearError } = productSlice.actions;
export default productSlice.reducer;

// Developed and Designed by Trophy Developers
